import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../data/api'
import { useAuth } from '../hooks'
import CountryCodeSelect from '../components/countryCodeSelect'
import ConfirmModal from '../components/confirmModal'
import BillingDetailsForm from '../components/billingDetailsForm'
import TransactionsTab from '../components/transactionsTab'
import BuyCreditsModal from '../components/buyCreditsModal'

async function uploadViaSignedUrl(file, type) {
  const { uploadUrl, filePath } = await api.post('/api/upload/signed-url', { type, contentType: file.type })
  await api.putFile(uploadUrl, file)
  return filePath
}

function Toast({ toast }) {
  if (!toast) return null
  return (
    <div className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border text-sm shadow-2xl animate-fade-in whitespace-nowrap ${
      toast.type === 'error'
        ? 'border-red-500/40 text-red-400'
        : 'border-emerald-500/40 text-emerald-300'
    }`} style={{ background: toast.type === 'error' ? '#3b0d0d' : '#0d2b1f' }}>
      <span>{toast.type === 'error' ? '✗' : '✓'}</span>
      {toast.msg}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
             style={{ color: 'var(--text-3)' }}>{label}</label>
      {children}
    </div>
  )
}

function TextInput({ value, onChange, readOnly, placeholder, type = 'text', maxLength }) {
  return (
    <input
      type={type} value={value} readOnly={readOnly} placeholder={placeholder}
      maxLength={maxLength}
      onChange={onChange ? e => onChange(e.target.value) : undefined}
      className="w-full px-3 py-2.5 rounded-xl text-sm outline-none transition-all"
      style={{
        background:  'var(--surface-2)',
        border:      '1px solid var(--border)',
        color:       readOnly ? 'var(--text-3)' : 'var(--text-1)',
        cursor:      readOnly ? 'default' : 'text',
      }}
      onFocus={readOnly ? undefined : e => e.currentTarget.style.borderColor = 'var(--brand)'}
      onBlur={readOnly  ? undefined : e => e.currentTarget.style.borderColor = 'var(--border)'}
    />
  )
}

// ── Tab: General ──────────────────────────────────────────────────────────────
function GeneralTab({ user, updateUser, showToast }) {
  const [name,        setName]        = useState(user.name || '')
  const [countryCode, setCountryCode] = useState(user.phone?.countryCode || '+91')
  const [phone,       setPhone]       = useState(user.phone?.number || '')
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState('')

  async function handleSave() {
    if (!name.trim()) return setError('Name is required.')
    setError(''); setSaving(true)
    try {
      const updated = await api.patch('/api/users/me', {
        name: name.trim(),
        phone: phone.trim() ? { countryCode, number: phone.trim() } : { countryCode: '', number: '' },
      })
      updateUser(updated)
      showToast('Profile saved successfully.')
    } catch (err) { showToast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="space-y-5">
      <Field label="Name">
        <TextInput value={name} onChange={v => { setName(v); setError('') }} maxLength={80} />
      </Field>
      <Field label="Email">
        <TextInput value={user.email} readOnly />
      </Field>
      <Field label="Mobile Number">
        <div className="flex gap-2">
          <CountryCodeSelect value={countryCode} onChange={setCountryCode} />
          <TextInput value={phone} onChange={v => setPhone(v.replace(/\D/g, ''))} placeholder="Phone number" maxLength={15} />
        </div>
      </Field>
      {error && <p className="text-xs" style={{ color: 'var(--red)' }}>{error}</p>}
      <div className="flex justify-end pt-1">
        <button onClick={handleSave} disabled={saving || !name.trim()} className="btn-primary text-sm">
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  )
}

// ── Tab: Security ─────────────────────────────────────────────────────────────
function SecurityTab({ showToast }) {
  const [currentPwd, setCurrentPwd] = useState('')
  const [newPwd,     setNewPwd]     = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState('')

  async function handleChange() {
    if (!currentPwd || !newPwd || !confirmPwd) return setError('All fields are required.')
    if (newPwd.length < 8)       return setError('New password must be at least 8 characters.')
    if (newPwd !== confirmPwd)   return setError('Passwords do not match.')
    setError(''); setSaving(true)
    try {
      await api.patch('/api/users/me/password', { currentPassword: currentPwd, newPassword: newPwd })
      showToast('Password changed successfully.')
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('')
    } catch (err) { showToast(err.message, 'error') }
    finally { setSaving(false) }
  }

  return (
    <div className="space-y-5">
      <Field label="Current Password">
        <TextInput type="password" value={currentPwd} onChange={v => { setCurrentPwd(v); setError('') }} maxLength={20} />
      </Field>
      <Field label="New Password">
        <TextInput type="password" value={newPwd} onChange={v => { setNewPwd(v); setError('') }} maxLength={20} />
      </Field>
      <Field label="Confirm New Password">
        <TextInput type="password" value={confirmPwd} onChange={v => { setConfirmPwd(v); setError('') }} maxLength={20} />
      </Field>
      {error && <p className="text-xs" style={{ color: 'var(--red)' }}>{error}</p>}
      <div className="flex justify-end pt-1">
        <button onClick={handleChange} disabled={saving} className="btn-primary text-sm">
          {saving ? 'Updating…' : 'Update Password'}
        </button>
      </div>
    </div>
  )
}

// ── Tab: Certificate ──────────────────────────────────────────────────────────
function CertificateTab({ user, updateUser, showToast }) {
  const [certUrl,   setCertUrl]   = useState(user.certificateTemplate || null)
  const [certName,  setCertName]  = useState(user.certificateTemplateName || null)
  const [uploading,    setUploading]    = useState(false)
  const [deleting,     setDeleting]     = useState(false)
  const [confirmDel,   setConfirmDel]   = useState(false)
  const certRef = useRef(null)

  const MAX_CERT_SIZE = 10 * 1024 * 1024 // 10 MB

  async function handleChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > MAX_CERT_SIZE) {
      showToast('PDF must be under 10 MB.', 'error')
      e.target.value = ''
      return
    }
    setUploading(true)
    try {
      const filePath = await uploadViaSignedUrl(file, 'certificateTemplate')
      const updated  = await api.patch('/api/users/me', { certificateTemplate: filePath, certificateTemplateName: file.name })
      updateUser(updated)
      setCertUrl(updated.certificateTemplate)
      setCertName(file.name)
      showToast('Certificate template updated.')
    } catch (err) { showToast(err.message, 'error') }
    finally { setUploading(false) }
  }

  async function handleDelete() {
    setDeleting(true)
    try {
      const updated = await api.patch('/api/users/me', { certificateTemplate: null, certificateTemplateName: null })
      updateUser(updated)
      setCertUrl(null)
      setCertName(null)
      showToast('Certificate template removed.')
    } catch (err) { showToast(err.message, 'error') }
    finally { setDeleting(false) }
  }

  return (
    <>
    {confirmDel && (
      <ConfirmModal
        title="Delete Certificate Template"
        message="Are you sure you want to delete this certificate template? This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={() => { setConfirmDel(false); handleDelete() }}
        onCancel={() => setConfirmDel(false)}
      />
    )}
    <div className="space-y-4">
      <p className="text-sm mb-3" style={{ color: 'var(--text-3)' }}>
        Upload a PDF template (max 10 MB). The examinee's name will be filled in automatically when they pass an exam.
      </p>

      {/* Sample template link */}
      <div className="flex items-center justify-between px-4 py-3 rounded-xl"
           style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2.5">
          <svg viewBox="0 0 16 16" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--brand)' }}>
            <rect x="2" y="1" width="12" height="14" rx="1.5"/>
            <path d="M5 5h6M5 8h6M5 11h4"/>
          </svg>
          <span className="text-sm font-medium" style={{ color: 'var(--text-2)' }}>Sample Certificate Template</span>
        </div>
        <a href="/completionCertificateTemplate.pdf" target="_blank" rel="noopener noreferrer"
           className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
           style={{ color: 'var(--brand)' }}>
          View PDF ↗
        </a>
      </div>
      {certUrl ? (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl"
             style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)' }}>
          <svg viewBox="0 0 16 16" className="w-4 h-4 flex-shrink-0" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2.5 8.5l3.5 3.5 7-7"/>
          </svg>
          <span className="text-sm font-medium flex-1 truncate" style={{ color: '#34d399' }}>
            {certName || 'template.pdf'}
          </span>
          <a href={certUrl} target="_blank" rel="noopener noreferrer"
             className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
             style={{ color: 'var(--brand)' }}>
            Preview ↗
          </a>
          <button onClick={() => certRef.current?.click()} disabled={uploading || deleting}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors"
            style={{ color: 'var(--brand)', borderColor: 'var(--brand)' }}>
            Replace
          </button>
          <button onClick={() => setConfirmDel(true)} disabled={uploading || deleting}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors"
            style={{ color: '#f87171', borderColor: '#f87171' }}>
            {deleting ? 'Removing…' : 'Delete'}
          </button>
        </div>
      ) : (
        <button onClick={() => certRef.current?.click()} disabled={uploading}
          className="w-full flex flex-col items-center gap-2 px-4 py-16 rounded-xl border-2 border-dashed transition-colors"
          style={{ borderColor: 'var(--border-2)', color: 'var(--text-3)' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)' }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-2)'; e.currentTarget.style.color = 'var(--text-3)' }}>
          <span className="text-3xl">📄</span>
          <span className="text-sm font-medium">{uploading ? 'Uploading…' : 'Click to upload template'}</span>
          <span className="text-xs">PDF only · max 10 MB</span>
        </button>
      )}
      <input ref={certRef} type="file" accept="application/pdf"
             className="hidden" onChange={handleChange} />
    </div>
    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ProfilePage() {
  const { user, updateUser } = useAuth()
  const navigate = useNavigate()
  const picRef = useRef(null)

  const [picUrl,       setPicUrl]       = useState(user.profilePic || null)
  const [picUploading, setPicUploading] = useState(false)
  const [toast,        setToast]        = useState(null)
  const [activeTab,    setActiveTab]    = useState('general')
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false)

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  const hasOwnBalance = user.role === 'organisation' || (user.role === 'user' && !user.orgId)

  // The balance in context can go stale — it's only ever refreshed by an
  // action this same browser session takes (e.g. a Buy Credits purchase),
  // not by usage/deductions that happen elsewhere (an examinee's Trial or
  // exam attempt, generation on another tab, etc.). Refetch it on mount so
  // this page always shows what's actually in the database.
  useEffect(() => {
    if (!hasOwnBalance) return
    api.get('/api/users/me').then(me => {
      if (me.creditsBalance != null) updateUser({ ...user, creditsBalance: me.creditsBalance })
    }).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tabs = [
    { id: 'general',     label: 'General' },
    { id: 'security',    label: 'Security' },
    ...(hasOwnBalance ? [{ id: 'billing', label: 'Billing Details' }] : []),
    ...(hasOwnBalance ? [{ id: 'invoices', label: 'Transactions' }] : []),
    // ...(user.role === 'organisation' ? [{ id: 'certificate', label: 'Certificate' }] : []),
  ]

  const initials  = (user.name || user.email).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const roleLabel = user.role === 'organisation' ? 'Organisation' : user.role === 'superadmin' ? 'Super Admin' : 'Examinee'
  const roleColor = user.role === 'organisation' ? 'rgba(139,92,246,0.15)' : user.role === 'superadmin' ? 'rgba(251,191,36,0.15)' : 'rgba(14,165,233,0.15)'
  const roleText  = user.role === 'organisation' ? '#a78bfa' : user.role === 'superadmin' ? '#fbbf24' : 'var(--brand)'

  async function handlePicChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setPicUploading(true)
    try {
      const filePath = await uploadViaSignedUrl(file, 'profilePic')
      const updated  = await api.patch('/api/users/me', { profilePic: filePath })
      updateUser(updated)
      setPicUrl(updated.profilePic) // signed read URL from response
      showToast('Profile picture updated.')
    } catch (err) { showToast(err.message, 'error') }
    finally { setPicUploading(false) }
  }

  return (
    <div className="w-full px-6 sm:px-40 py-8 animate-fade-up">
      <Toast toast={toast} />

      {buyCreditsOpen && (
        <BuyCreditsModal user={user} updateUser={updateUser} showToast={showToast} onClose={() => setBuyCreditsOpen(false)} />
      )}

      {/* Back + title */}
      <div className="flex items-center gap-3 mb-8">
        <button onClick={() => navigate(-1)} className="btn-ghost text-sm">← Back</button>
        <h1 className="text-2xl font-black" style={{ color: 'var(--text-1)' }}>My Profile</h1>
      </div>

      {/* Header card */}
      <div className="card p-6 flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-4">
        {/* Avatar */}
        <div className="relative group cursor-pointer flex-shrink-0" onClick={() => picRef.current?.click()}>
          {picUrl ? (
            <img src={picUrl} alt="Profile" className="w-20 h-20 rounded-full object-cover"
                 style={{ border: '3px solid var(--brand)' }} />
          ) : (
            <div className="w-20 h-20 rounded-full flex items-center justify-center text-xl font-black"
                 style={{ background: 'var(--brand)', color: '#fff' }}>
              {initials}
            </div>
          )}
          <div className="absolute inset-0 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
               style={{ background: 'rgba(0,0,0,0.55)', color: '#fff' }}>
            <span className="text-xs font-semibold">{picUploading ? '…' : '📷'}</span>
          </div>
          <div className="absolute bottom-0 right-0 w-5 h-5 rounded-full flex items-center justify-center shadow"
               style={{ background: 'var(--brand)', border: '2px solid var(--surface)' }}>
            <svg viewBox="0 0 12 12" className="w-2 h-2" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 1.5l2.5 2.5-6 6H2V7.5l6-6z"/>
            </svg>
          </div>
        </div>
        <input ref={picRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePicChange} />

        {/* Info */}
        <div className="flex-1 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
            <p className="text-xl font-black" style={{ color: 'var(--text-1)' }}>{user.name}</p>
            <span className="inline-flex items-center self-center text-xs font-semibold px-2.5 py-0.5 rounded-full"
                  style={{ background: roleColor, color: roleText }}>
              {roleLabel}
            </span>
          </div>
          <p className="text-sm mb-3" style={{ color: 'var(--text-3)' }}>{user.email}</p>
          <div className="flex flex-wrap justify-center sm:justify-start gap-4">
            {user.phone?.number && (
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                📱 {user.phone.countryCode} {user.phone.number}
              </span>
            )}
            {user.role !== 'superadmin' && user.createdAt && (
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                🗓 Joined {new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
            {user.role !== 'superadmin' && (
              <span className="text-xs" style={{ color: user.status === 'approved' ? 'var(--green)' : 'var(--amber)' }}>
                {user.status === 'approved' ? '✅ Approved' : '⏳ Pending'}
              </span>
            )}
            {user.role === 'user' && user.accountExpiresAt && (() => {
              const daysLeft = Math.ceil((new Date(user.accountExpiresAt) - new Date()) / 86400000)
              const color    = daysLeft <= 0 ? '#f87171' : daysLeft <= 30 ? '#fbbf24' : 'var(--text-3)'
              const label    = new Date(user.accountExpiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
              return (
                <span className="text-xs" style={{ color }}>
                  {daysLeft <= 0 ? '⚠ Expired' : '📅 Valid till'} {label}
                </span>
              )
            })()}
          </div>
        </div>
      </div>

      {/* Credits — organisations and standalone users only; an org-linked
          examinee has no balance of their own, their org's does the billing. */}
      {hasOwnBalance && (
        <div className="card p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--text-3)' }}>Credit Balance</p>
            <p className="text-2xl font-black" style={{ color: 'var(--text-1)' }}>
              ₹{(user.creditsBalance ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <button onClick={() => navigate('/usage-history')} className="text-xs font-medium mt-1" style={{ color: 'var(--brand)' }}>
              View usage history →
            </button>
          </div>
          <button onClick={() => setBuyCreditsOpen(true)}
            className="btn-primary text-sm flex-shrink-0 flex items-center justify-center gap-1.5 w-full sm:w-auto">
            <svg viewBox="0 0 20 20" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="16" height="11" rx="1.8"/><path d="M2 8.5h16"/><path d="M5 12.5h3"/>
            </svg>
            Buy Credits
          </button>
        </div>
      )}

      {/* Tab card */}
      <div className="card">
        {/* Tab bar */}
        <div className="flex overflow-x-auto border-b rounded-t-2xl" style={{ borderColor: 'var(--border)' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="px-5 py-3.5 text-sm font-medium transition-all relative flex-shrink-0 whitespace-nowrap"
              style={{ color: activeTab === tab.id ? 'var(--brand)' : 'var(--text-3)' }}>
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                      style={{ background: 'var(--brand)' }} />
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="p-6">
          {activeTab === 'general'     && <GeneralTab     user={user} updateUser={updateUser} showToast={showToast} />}
          {activeTab === 'security'    && <SecurityTab    showToast={showToast} />}
          {activeTab === 'billing'     && <BillingDetailsForm user={user} updateUser={updateUser} showToast={showToast} />}
          {activeTab === 'invoices'    && <TransactionsTab user={user} showToast={showToast} />}
          {/* activeTab === 'certificate' && <CertificateTab user={user} updateUser={updateUser} showToast={showToast} /> */}
        </div>
      </div>

    </div>
  )
}
